var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { useEffect, useMemo, useState } from 'react';
import { callWidthSum, getScrollBarWidth, withCalculatedColumnWidth } from "../utils";
import { createGridRowRenderer } from "../renderers/defaultGridRow";
import { createGridHeaderRenderer } from "../renderers/defaultGridHeader";
var defaultRowRenderer = createGridRowRenderer();
var defaultHeaderRenderer = createGridHeaderRenderer();
export function useGridCoreConfigure(params) {
    var self = useState(function () { return ({
        tearDownCallbacks: [],
        columnsConfig: params.columns.map(function (col) { return (__assign(__assign({}, col), { width: col.width || 0 })); })
    }); })[0];
    useEffect(function () {
        return function () {
            self.tearDownCallbacks.forEach(function (cb) { return cb(); });
            self.tearDownCallbacks = [];
        };
    }, []);
    return useMemo(function () {
        var scrollBarWidth = getScrollBarWidth();
        self.tearDownCallbacks.forEach(function (cb) { return cb(); });
        self.tearDownCallbacks = [];
        var plugins = (params.plugins || []).map(function (create) {
            return create({
                api: params.gridApi,
                rootRef: params.rootRef,
                bodyRef: params.bodyRef,
                getColumns: function () { return self.columnsConfig; },
                getData: function () { return params.data; }
            });
        });
        plugins.forEach(function (plugin) {
            if (plugin.tearDown) {
                self.tearDownCallbacks.push(plugin.tearDown);
            }
        });
        var processedColumns = plugins
            .map(function (i) { return i.processColumns; })
            .reduce(function (acc, curr) {
            if (curr) {
                return curr(acc);
            }
            return acc;
        }, params.columns)
            .map(function (i, idx) { var _a; return (__assign(__assign({}, i), { order: (_a = i.order) !== null && _a !== void 0 ? _a : idx })); })
            .sort(function (a, b) { return a.order - b.order; });
        var availableBodyWidth = params.rootWidth - scrollBarWidth;
        self.columnsConfig = withCalculatedColumnWidth(processedColumns, availableBodyWidth);
        var rowWidth = Math.max(self.columnsConfig.reduce(callWidthSum, 0), availableBodyWidth);
        var idGetter = function (index) {
            return params.idGetter(params.data[index]);
        };
        var rowRenderer = plugins
            .map(function (i) { return i.rowRenderer; })
            .filter(Boolean)
            .reduce(function (acc, curr) {
            return function rowRendererDecorated(_a) {
                var index = _a.index, style = _a.style, height = _a.height, width = _a.width, topOffset = _a.topOffset, handlers = _a.handlers, disabled = _a.disabled, inactive = _a.inactive, _b = _a.className, className = _b === void 0 ? '' : _b;
                return curr({
                    data: params.data,
                    columns: self.columnsConfig,
                    idGetter: params.idGetter,
                    originalRenderer: acc,
                    disabled: disabled,
                    inactive: inactive,
                    handlers: handlers,
                    index: index,
                    height: height,
                    width: width,
                    topOffset: topOffset,
                    style: style,
                    className: className
                });
            };
        }, defaultRowRenderer);
        var headerRenderer = plugins
            .map(function (i) { return i.headerRenderer; })
            .filter(Boolean)
            .reduce(function (acc, curr) {
            return function headerRendererDecorated(_a) {
                var columns = _a.columns, containerRef = _a.containerRef, rowWidth = _a.rowWidth, style = _a.style, className = _a.className;
                return curr({
                    columns: columns,
                    containerRef: containerRef,
                    rowWidth: rowWidth + scrollBarWidth,
                    style: style,
                    className: className,
                    originalRenderer: acc
                });
            };
        }, defaultHeaderRenderer);
        var rowHeightGetters = plugins.map(function (i) { return i.rowHeightGetter; }).filter(Boolean);
        var lastRowHeightGetter = rowHeightGetters[rowHeightGetters.length - 1];
        var containerAddonRenderer = function () {
            return React.createElement(React.Fragment, { key: 'containerAddon' }, plugins
                .filter(function (i) { return i.containerAddons; })
                .reduce(function (acc, curr) {
                if (curr.containerAddons) {
                    return __spreadArrays(acc, curr.containerAddons);
                }
                return acc;
            }, [])
                .sort(function (a, b) { return (a.order || 0) - (b.order || 0); })
                .map(function (i) {
                return React.createElement(React.Fragment, { key: i.id }, i.render());
            }));
        };
        var bodyAddonRenderer = function () {
            return React.createElement(React.Fragment, { key: 'bodyAddon' }, plugins
                .filter(function (i) { return i.bodyAddons; })
                .reduce(function (acc, curr) {
                if (curr.bodyAddons) {
                    return __spreadArrays(acc, curr.bodyAddons);
                }
                return acc;
            }, [])
                .sort(function (a, b) { return (a.order || 0) - (b.order || 0); })
                .map(function (i) {
                return React.createElement(React.Fragment, { key: i.id }, i.render());
            }));
        };
        var subheaderAddonRenderer = function () {
            return React.createElement(React.Fragment, { key: 'subheaderAddon' }, plugins
                .filter(function (i) { return i.subheaderAddons; })
                .reduce(function (acc, curr) {
                if (curr.subheaderAddons) {
                    return __spreadArrays(acc, curr.subheaderAddons);
                }
                return acc;
            }, [])
                .sort(function (a, b) { return (a.order || 0) - (b.order || 0); })
                .map(function (i) {
                return React.createElement(React.Fragment, { key: i.id }, i.render());
            }));
        };
        var rootClassNames = plugins.reduce(function (acc, curr) {
            if (curr.rootClassNames) {
                return acc.concat(curr.rootClassNames);
            }
            return acc;
        }, []);
        if (!lastRowHeightGetter) {
            throw new Error('GridCore: you must define rowHeightGetter in plugins');
        }
        var hasColumns = !!self.columnsConfig.filter(function (i) { return !i.isService; }).length;
        return {
            idGetter: idGetter,
            hasColumns: hasColumns,
            rowRenderer: rowRenderer,
            header: headerRenderer({
                columns: self.columnsConfig,
                containerRef: params.headerContainerRef,
                rowWidth: rowWidth,
                className: '',
                style: {}
            }),
            rowHeightGetter: lastRowHeightGetter,
            rowWidth: rowWidth,
            containerAddonRenderer: containerAddonRenderer,
            bodyAddonRenderer: bodyAddonRenderer,
            subheaderAddonRenderer: subheaderAddonRenderer,
            rootClassNames: rootClassNames
        };
    }, [params.plugins, params.data, params.columns, params.rootWidth]);
}
//# sourceMappingURL=useGridCoreConfigure.js.map